import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { KeyboardShortcut } from '@repo/components/tooltips';
import { metaTrigger } from '@repo/utils/helpers/keyboardHandler';
import Search from './Search';
import PrivateGroups from './Groups';
import NavSection from './NavSection';
import styles from './Navigation.module.scss';
import { Logo, NavHomeIcon, NavNotesIcon, NavPeopleIcon, PlusIcon } from '@repo/icons';
import { useDispatch, useSelector } from 'react-redux';
import { hideNavBar, navigateToSettings, startNewContact } from '@repo/redux/actions/ui';
import { selectGroupCollection } from '@repo/redux/actions/groups';
import UpgradeBanner from './UpgradeBanner';
import { useContactLimitExceeded } from '@app/Context/hooks/use-contact-limit-exceeded';
import ActivityNavItem from './ActivityNavItem';
import NexusButton from './NexusButton';
import NavigationItem, { NavigationItemDetails } from './NavigationItem';
import WorkspacePicker from './WorkspacePicker';
import { selectNavBarState } from '@repo/redux/reducers/ui/navBar';
import { HideNavBarButton } from './NavBarToggle';
import { selectUserHasFlag } from '@repo/redux/reducers/user/metadata';
import { NavBarResizer } from '@components/PanelResizer';

const navigationKeyCharTrigger = 'g';

const mainNav = [
  // this will be rendered separately in the main sidebar nav, but we'll want to include it here
  // for keyboard shortcut purposes and for Command Bar navigation
  {
    IconComponent: NavHomeIcon,
    text: 'Home',
    to: '/',
    keyChar: 'h',
    scaleOnHover: true,
  },
  {
    IconComponent: NavPeopleIcon,
    text: 'People',
    to: '/people',
    keyChar: 'p',
    scaleOnHover: true,
  },
  {
    IconComponent: NavNotesIcon,
    text: 'Notes',
    to: '/notes',
    keyChar: 'n',
    scaleOnHover: true,
  },
];

export const navigationItems: NavigationItemDetails[] = mainNav; //.concat(collectionsNav);

const keyForRoute = (routePath: string) =>
  routePath
    .split('/')
    .filter((_, idx) => idx < 3)
    .join('/');

export default function Navigation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hotKeyActive = useRef(false);
  const timeout = useRef(null);
  const contactLimitExceeded = useContactLimitExceeded();
  const groups = useSelector(selectGroupCollection);
  const showNotesInSidebar = useSelector(selectUserHasFlag('showNotesInSidebar'));
  const { status, transitioning, exiting } = useSelector(selectNavBarState);

  const orderedUserCreatedGroups = useMemo(() => {
    return groups.items
      .filter((group) => !!group.updated && !group.slug)
      .valueSeq()
      .toArray();
  }, [groups.items]);

  const mainNavItems = useMemo(() => {
    if (showNotesInSidebar) {
      return navigationItems;
    } else {
      return navigationItems.filter((item) => item.text !== 'Notes');
    }
  }, [showNotesInSidebar]);

  const { orderedRoutes, routeIndexes } = useMemo(() => {
    const orderedRoutes = [
      ...mainNavItems.map(({ to }) => to),
      '/group/starred',
      ...orderedUserCreatedGroups.map((group) => `/group/${group.slug || group.id}`),
    ];

    return {
      orderedRoutes,
      routeIndexes: orderedRoutes.reduce(
        (routeToIndex, route, index) => ({ ...routeToIndex, [route]: index }),
        {},
      ),
    };
  }, [mainNavItems, orderedUserCreatedGroups]);

  const openNewPersonModal = useCallback(() => dispatch(startNewContact()), []);
  const openSettingsModal = useCallback(() => dispatch(navigateToSettings()), []);

  const handleHotkey = (e) => {
    if (e.defaultPrevented) {
      return;
    }

    if (metaTrigger(e) && e.key === 'ArrowDown') {
      const routeKey = keyForRoute(location.pathname);
      const nextIndex = routeIndexes[routeKey] + 1;
      if (orderedRoutes[nextIndex]) {
        navigate(orderedRoutes[nextIndex]);
      }
      e.preventDefault();
    }

    if (metaTrigger(e) && e.key === 'ArrowUp') {
      const routeKey = keyForRoute(location.pathname);
      const nextIndex = routeIndexes[routeKey] - 1;
      if (orderedRoutes[nextIndex]) {
        navigate(orderedRoutes[nextIndex]);
      }
      e.preventDefault();
    }

    // Settings page nav
    if (e.key === ',' && metaTrigger(e)) {
      e.preventDefault();
      if (window.location?.pathname != '/settings') {
        openSettingsModal();
      }
    }

    if (e.key === navigationKeyCharTrigger) {
      clearHotKey();
      timeout.current = setTimeout(clearHotKey, 3000);
      hotKeyActive.current = true;
      window.__activeAction = true;
    }
  };

  const clearHotKey = useCallback(() => {
    hotKeyActive.current = false;
    window.__activeAction = false;

    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  }, []);

  const navViaHotkey = (to: string) => {
    navigate(to);
    clearHotKey();
  };

  const handleShortcut = (e) => {
    if (!hotKeyActive.current) {
      return;
    }

    for (const item of navigationItems) {
      if (item.keyChar === e.key) {
        e.preventDefault();
        return navViaHotkey(item.to);
      }
    }

    if (e.key === ',') {
      e.preventDefault();
      openSettingsModal();
      return clearHotKey();
    }

    if (e.key === 's') {
      e.preventDefault();
      return navViaHotkey('/group/starred');
    }

    const keyNum = Number.parseInt(e.key);
    if (!Number.isNaN(keyNum)) {
      const group = orderedUserCreatedGroups[keyNum - 1];
      if (group) {
        e.preventDefault();
        return navViaHotkey(`/group/${group.id}`);
      }
    }
  };

  const addNewPerson = useCallback((e) => {
    e.preventDefault();
    openNewPersonModal();
  }, []);

  useEffect(() => {
    window.app.on('hotkey', handleHotkey);
    window.app.on('keydown', handleShortcut);
    return () => {
      window.app.off('hotkey', handleHotkey);
      window.app.off('keydown', handleShortcut);
    };
  }, [handleHotkey, handleShortcut]);

  useEffect(() => {
    if (window.__activeAction) {
      clearHotKey();
    }
  }, [location.pathname]);

  return (
    <div
      className={cn(
        styles.navBar,
        styles[`navBar-${status}`],
        transitioning && styles[`${exiting}-exiting`],
      )}
    >
      <nav className={styles.container}>
        <div className={styles.navBarStatusToggle}>
          <Logo width="30" height="18" className={styles.clayLogo} />
          <HideNavBarButton onClick={() => dispatch(hideNavBar())} />
        </div>
        <WorkspacePicker />
        <Search />
        <div className={styles.primary}>
          <NavSection className={styles.navigation}>
            {mainNavItems.map((props, i) => {
              if (props.text === 'Home') {
                return <ActivityNavItem key={i} />;
              }

              return (
                <NavigationItem key={i} id={`${props.text.toLowerCase()}-navigation`} {...props} />
              );
            })}
          </NavSection>
          <PrivateGroups />
          <UpgradeBanner />
        </div>
        <div className={styles.actions}>
          <KeyboardShortcut keys="P" className={styles.actionPopupOffset}>
            <div
              onClick={contactLimitExceeded ? undefined : addNewPerson}
              className={cn(
                styles.action,
                styles.newPersonAction,
                contactLimitExceeded && styles.disabled,
              )}
              id="new-person-btn"
            >
              <PlusIcon
                className={styles.navigationIcon}
                preserveAspectRatio="xMinYMid meet"
                strokeWidth={1.6}
                color="textPrimary"
              />
              <span className={styles.actionText}>New Person</span>
            </div>
          </KeyboardShortcut>
          <NexusButton />
        </div>
      </nav>
      <NavBarResizer />
    </div>
  );
}
